'use client';

import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';

export default function ScrollOverlay() {
  const t = useTranslations('LandingPage');

  return (
    <div className="relative z-10 w-full flex flex-col items-center">
      {/* Section 1: Hero */}
      <section className="w-full min-h-screen flex flex-col justify-center items-start px-[2rem] md:px-[6rem] max-w-[80rem]">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="max-w-[36rem] p-[2rem] rounded-[1.75rem] bg-zinc-950/50 dark:bg-zinc-950/50 backdrop-blur-md border border-zinc-800/80 dark:border-zinc-800/80 shadow-2xl"
        >
          <span className="text-[0.875rem] font-medium tracking-wider text-pink-primary dark:text-pink-primary uppercase mb-[0.5rem] block">
            {t('title')}
          </span>
          <h1 className="text-[2.5rem] md:text-[3.5rem] font-bold leading-tight text-zinc-50 dark:text-zinc-50 tracking-tight mb-[1rem]">
            {t('subtitle')}
          </h1>
          <p className="text-[1rem] md:text-[1.125rem] text-zinc-400 dark:text-zinc-400 mb-[2rem]">
            {t('section1.description')}
          </p>
          <div className="flex items-center gap-[1rem]">
            <button 
              className="px-[2rem] py-[0.75rem] bg-pink-primary hover:bg-pink-secondary active:scale-[0.98] disabled:opacity-50 text-white font-medium rounded-full transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-primary outline-none"
              aria-label={t('cta')}
            >
              {t('cta')}
            </button>
          </div>
        </motion.div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-[2rem] left-1/2 -translate-x-1/2 flex flex-col items-center gap-[0.5rem] pointer-events-none">
          <span className="text-[0.75rem] tracking-widest text-zinc-500 uppercase">
            {t('scrollPrompt')}
          </span>
          <div className="w-[1.5rem] h-[2.5rem] rounded-full border-2 border-zinc-800 flex justify-center p-[0.25rem]">
            <motion.div 
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
              className="w-[0.375rem] h-[0.375rem] rounded-full bg-pink-primary"
            />
          </div>
        </div>
      </section>

      {/* Section 2: Fluid Geometry */}
      <section className="w-full min-h-screen flex flex-col justify-center items-end px-[2rem] md:px-[6rem] max-w-[80rem]">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          className="max-w-[36rem] p-[2rem] rounded-[1.75rem] bg-zinc-950/50 dark:bg-zinc-950/50 backdrop-blur-md border border-zinc-800/80 dark:border-zinc-800/80 shadow-2xl"
        >
          <h2 className="text-[2rem] font-bold text-zinc-50 dark:text-zinc-50 tracking-tight mb-[1rem]">
            {t('section1.title')}
          </h2>
          <p className="text-[1rem] text-zinc-400 dark:text-zinc-400">
            {t('section1.description')}
          </p>
        </motion.div>
      </section>

      {/* Section 3: Material 3 Design */}
      <section className="w-full min-h-screen flex flex-col justify-center items-start px-[2rem] md:px-[6rem] max-w-[80rem]">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          className="max-w-[36rem] p-[2rem] rounded-[1.75rem] bg-zinc-950/50 dark:bg-zinc-950/50 backdrop-blur-md border border-zinc-800/80 dark:border-zinc-800/80 shadow-2xl"
        >
          <h2 className="text-[2rem] font-bold text-zinc-50 dark:text-zinc-50 tracking-tight mb-[1rem]">
            {t('section2.title')}
          </h2>
          <p className="text-[1rem] text-zinc-400 dark:text-zinc-400">
            {t('section2.description')}
          </p>
        </motion.div>
      </section>

      {/* Section 4: Premium Experience */}
      <section className="w-full min-h-screen flex flex-col justify-center items-center text-center px-[2rem] max-w-[80rem] mb-[5rem]">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          className="max-w-[40rem] p-[3rem] rounded-[1.75rem] bg-zinc-950/50 dark:bg-zinc-950/50 backdrop-blur-md border border-zinc-800/80 dark:border-zinc-800/80 shadow-2xl"
        >
          <h2 className="text-[2.25rem] font-bold text-zinc-50 dark:text-zinc-50 tracking-tight mb-[1rem]">
            {t('section3.title')}
          </h2>
          <p className="text-[1.125rem] text-zinc-400 dark:text-zinc-400 mb-[2rem]">
            {t('section3.description')}
          </p>
          <button 
            className="px-[2.5rem] py-[1rem] bg-pink-primary hover:bg-pink-secondary active:scale-[0.98] disabled:opacity-50 text-white font-medium rounded-full shadow-lg shadow-pink-500/20 transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-primary outline-none"
            aria-label={t('cta')}
          >
            {t('cta')}
          </button>
        </motion.div>
      </section>
    </div>
  );
}
